/* ResetPassword — consume the token from /auth/reset/{token}, set a new
   password, kick the user to login. */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { Loader2, Lock, ArrowRight, Check, AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — please sign in.");
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-[1fr_1.1fr]">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[420px]" data-testid="reset-password-page">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-xl bg-foreground text-accent grid place-items-center font-bold">v</div>
            <span className="font-semibold tracking-tight">verqo</span>
          </Link>

          {!done ? (
            <>
              <h1 className="display text-4xl sm:text-5xl mt-2 leading-[1.05] tracking-tight">Set a new password</h1>
              <p className="text-foreground/65 mt-3 text-[14px]">Choose something strong — at least 8 characters.</p>
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <label className="text-[12px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5 block">New password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-foreground/40 absolute top-1/2 -translate-y-1/2 left-3"/>
                    <input
                      type={show ? "text" : "password"}
                      autoFocus
                      required
                      value={pw}
                      onChange={e => setPw(e.target.value)}
                      data-testid="reset-new-password"
                      className="w-full h-12 rounded-xl border border-foreground/15 bg-white pl-10 pr-10 text-sm focus:outline-none focus:border-foreground/45"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(v => !v)}
                      data-testid="reset-toggle-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/45 hover:text-foreground"
                    >
                      {show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[12px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5 block">Confirm new password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-foreground/40 absolute top-1/2 -translate-y-1/2 left-3"/>
                    <input
                      type={show ? "text" : "password"}
                      required
                      value={pw2}
                      onChange={e => setPw2(e.target.value)}
                      data-testid="reset-confirm-password"
                      className="w-full h-12 rounded-xl border border-foreground/15 bg-white pl-10 pr-3 text-sm focus:outline-none focus:border-foreground/45"
                    />
                  </div>
                </div>
                {err && (
                  <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[12.5px] text-rose-800 inline-flex items-start gap-2" data-testid="reset-error">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>{err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit"
                  className="w-full h-12 rounded-full bg-foreground text-background font-medium inline-flex items-center justify-center gap-2 hover:bg-foreground/90 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
                  {loading ? "Saving…" : <>Update password <ArrowRight className="w-3.5 h-3.5"/></>}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-success">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 grid place-items-center mb-5">
                <Check className="w-5 h-5 text-emerald-700"/>
              </div>
              <h1 className="display text-3xl leading-[1.1] tracking-tight">Password updated</h1>
              <p className="text-foreground/65 mt-3 text-[14px]">
                You've been signed out of every active session. Redirecting you to sign in…
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="hidden md:block bg-foreground"/>
    </div>
  );
}
